import {
	Communication,
	ProviderCardProps,
} from 'domain/usecases/private/communications/get-communications';
import {PatchCommunicationParams} from 'domain/usecases/private/communications/patch-communication';
import {
	makePatchCommunication,
	// mockedMakePatchCommunication as makePatchCommunication,
	// mockedMakePatchCommunicationWithBadRequestError as makePatchCommunication,
	// mockedMakePatchCommunicationWithNotFoundError as makePatchCommunication,
} from 'main/factories/usecases/private/communications/patch-communication-factory';
import {FeaturesEnum, HomeEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {
	makeGetCommunications,
	// mockedMakeGetCommunications as makeGetCommunications,
	// mockedMakeGetCommunicationsWithBadRequestError as makeGetCommunications,
	// mockedMakeGetCommunicationsWithNotFoundError as makeGetCommunications,
} from 'main/factories/usecases/private/communications/get-communications-factory';
import {useTheme} from '@mui/material';
import {useAccountInformation} from '../../hooks';
import {ProviderEnum} from '../constants/providerEnum';
import {formatDetail} from '../utils/formatDetail';
import {FormatType} from '../constants/FormatType';
import {useGetCommunicationsQuery} from '../queries/useGetCommunicationsQuery';
import {useConfirmCommunicationHasErrorStore} from '../store/useConfirmCommunicationHasErrorStore/index';
import {useConfirmCommunicationMutation} from '../queries/useConfirmCommunicationMutation';

const {newProviderMessage} = translations['pt-br'].privateFlow.communications;
const getCommunicationsFactory = makeGetCommunications();
const patchCommunicationFactory = makePatchCommunication();

export const useCommunications = () => {
	const [currentCommunicationIndexState, setCurrentCommunicationIndexState] = useState<number>(0);
	const [isCheckboxRequiredState, setIsCheckboxRequiredState] = useState<boolean>(false);
	const [termsCheckedState, setTermsCheckedState] = useState<boolean>(false);
	const navigate = useNavigate();
	const theme = useTheme();
	const {isHolder} = useAccountInformation();

	const confirmCommunicationHasError = useConfirmCommunicationHasErrorStore(
		state => state.confirmCommunicationHasError,
	);

	const {isEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_RN_585);

	const getCommunicationsQuery = useGetCommunicationsQuery({isEnabled}, getCommunicationsFactory);

	const communicationsResponse = isHolder ? (getCommunicationsQuery?.data ?? []) : [];

	const handleRefreshCommunicationsState = () => {
		getCommunicationsQuery.refetch();
	};

	const goToInitialPage = () => {
		navigate(HomeEnum.HOME_PAGE);
	};

	const confirmCommunicationStatus = ({
		currentCommunicationIndex,
		communicationsLength,
	}: {
		currentCommunicationIndex: number;
		communicationsLength: number;
	}) => {
		const isLastCommunication = currentCommunicationIndex >= communicationsLength - 1;
		const isSingleCommunication = communicationsLength === 1;

		return {
			isLastCommunication,
			isSingleCommunication,
		};
	};

	const confirmCommunicationSuccess = () => {
		const {isLastCommunication, isSingleCommunication} = confirmCommunicationStatus({
			currentCommunicationIndex: currentCommunicationIndexState,
			communicationsLength: communicationsResponse.length,
		});

		if (!isLastCommunication) {
			setCurrentCommunicationIndexState(prevIndex => prevIndex + 1);
		}

		if (isSingleCommunication || isLastCommunication) {
			goToInitialPage();
		}

		setIsCheckboxRequiredState(false);
		setTermsCheckedState(false);
	};

	const confirmCommunicationMutation = useConfirmCommunicationMutation(
		patchCommunicationFactory,
		{
			onSuccess: confirmCommunicationSuccess,
		},
	);

	const handleConfirmCommunication = (params: PatchCommunicationParams) => {
		if (!termsCheckedState) {
			setIsCheckboxRequiredState(true);
			return;
		}
		confirmCommunicationMutation.mutate(params);
	};

	const transformProvider = (
		communication: Communication,
		type: 'current' | 'new',
	): ProviderCardProps['provider'] => {
		const isCurrent = type === ProviderEnum.CURRENT_PROVIDER;
		const provider = isCurrent
			? communication?.prestadorAtual
			: communication?.prestadorSubstituto;

		const date = isCurrent
			? communication?.dtEncerramentoAtendimento || '-'
			: communication?.dtInicioAtendSubstituto || '-';

		return {
			type,
			message: isCurrent ? communication?.dsComunicado : newProviderMessage,
			date: date.split(' ')[0],
			details: {
				name: provider?.nmPrestador || '-',
				cnpj: formatDetail(provider?.nuCgcCpf, FormatType.CNPJ),
				address: provider?.formattedAddress || '-',
				...(!isCurrent && {
					phone: formatDetail(provider?.nuTelefone ?? '', FormatType.PHONE),
					email: provider?.dsEmail || '-',
				}),
			},
		};
	};

	const currentCommunication = communicationsResponse[currentCommunicationIndexState];

	const checkboxColor = isCheckboxRequiredState
		? theme.palette.error.main
		: theme.palette.text.primary;

	return {
		currentCommunicationIndexState,
		isCheckboxRequiredState,
		transformProvider,
		termsCheckedState,
		setTermsCheckedState,
		goToInitialPage,
		handleConfirmCommunication,
		confirmCommunicationMutation,
		communicationsResponse,
		getCommunicationsQuery,
		handleRefreshCommunicationsState,
		confirmCommunicationHasError,
		currentCommunication,
		checkboxColor,
	};
};
